import React, {useMemo} from 'react';
import {Box, styled, IconButton as MuiIconButton} from '@mui/material';
import {
  Body,
  Icon,
  designSystemToken,
  IconName,
} from '@lightricks/react-design-system';

interface VoiceCardProps {
  testID?: string;
  id: string;
  name: string;
  description?: string;
  isSelected?: boolean;
  isPlaying?: boolean;
  onSelect?: (id: string) => void;
  onPreview?: (id: string) => void;
}

const Container = styled(Box, {shouldForwardProp: (p) => p !== 'isSelected'})<{
  isSelected?: boolean;
}>(({isSelected}) => ({
  'display': 'flex',
  'padding': '12px',
  'flexDirection': 'column',
  'alignItems': 'flex-start',
  'gap': '4px',
  'borderRadius': '12px',
  'border': `1px solid`,
  'borderColor': isSelected
    ? designSystemToken('semantic.fg.primary')
    : designSystemToken('semantic.bg.neutral-inverse'),
  'transition': 'border-color 0.2s ease',
  'cursor': 'pointer',
  'backgroundColor': designSystemToken('semantic.fg.inverse'),
  '&:hover': {
    backgroundColor: designSystemToken('semantic.bg.tertiary'),
  },
}));

const ContentContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  alignSelf: 'stretch',
}));

const PlayButtonContainer = styled(MuiIconButton)(() => ({
  display: 'flex',
  minWidth: '32px',
  minHeight: '32px',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '4px',
}));

const TextContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
}));

function VoiceCard({
  testID = 'voice-card',
  id,
  name,
  description,
  isSelected,
  isPlaying,
  onSelect,
  onPreview,
}: VoiceCardProps) {
  const playButtonSettings = useMemo(() => {
    const settings: {backgroundColor: string; icon: IconName} = {
      backgroundColor: designSystemToken('semantic.bg.neutral-inverse'),
      icon: 'Actions-Play',
    };

    if (isPlaying && !isSelected) {
      settings.backgroundColor = designSystemToken(
        'semantic.bg.brand-secondary'
      );
      settings.icon = 'Features-Voice';
    } else if (isPlaying && isSelected) {
      settings.icon = 'Actions-Pause';
    }

    return settings;
  }, [isSelected, isPlaying]);

  return (
    <Container
      data-testid={testID}
      isSelected={isSelected}
      onClick={() => onSelect?.(id)}
    >
      <ContentContainer>
        <PlayButtonContainer
          aria-label={isPlaying ? 'pause' : 'play'}
          onClick={(e) => {
            e.stopPropagation();
            onPreview?.(id);
          }}
          sx={{
            'backgroundColor': playButtonSettings.backgroundColor,
            '&:hover': {
              backgroundColor: playButtonSettings.backgroundColor,
            },
          }}
        >
          <Icon
            name={playButtonSettings.icon}
            appearance="neutral"
            size="small"
          />
        </PlayButtonContainer>
        <TextContainer sx={{flex: 1}}>
          <Body size="lg" color={designSystemToken('semantic.fg.primary')}>
            {name}
          </Body>
          <Body size="sm" color={designSystemToken('semantic.fg.secondary')}>
            {description}
          </Body>
        </TextContainer>
      </ContentContainer>
    </Container>
  );
}

export default VoiceCard;
